import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, AlertTriangle, Ban, Scale, TrendingDown, CheckCircle, XCircle, PoundSterling } from 'lucide-react';

const HowPCPIsMisSoldPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                How PCP Car Finance is Mis-sold
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Understanding Discretionary Commission Arrangements and how they affected consumers
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed mb-6">
                PCP (Personal Contract Purchase) car finance may have been mis-sold if you were not 
                properly informed about the commission arrangements between the car dealer and the 
                finance lender. The primary concern relates to <strong>Discretionary Commission Arrangements 
                (DCAs)</strong>, which the Financial Conduct Authority (FCA) banned in January 2021.
              </p>
            </div>
          </div>
        </section>

        {/* Understanding DCAs Section */}
        <section className="py-16 lg:py-20 bg-destructive/5">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  Understanding Discretionary Commission Arrangements
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                Discretionary Commission Arrangements (DCAs) were agreements between lenders and car 
                dealers that allowed the dealer to influence the interest rate on your car finance:
              </p>

              <div className="space-y-4">
                <Card className="border-l-4 border-l-destructive">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>The lender would set a minimum interest rate</strong> — the base rate they were willing to offer.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>The dealer had discretion to increase this rate</strong> — they could add percentage points on top.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-destructive">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>The higher the rate, the more commission</strong> — dealers earned more by charging you more.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <p className="text-foreground">
                      <strong>Consumers were not told</strong> — in many cases, you had no idea this was happening.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Conflict of Interest Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <Ban className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  The Conflict of Interest
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-8">
                DCAs created a clear conflict of interest. The dealer had a financial incentive to 
                charge you a higher interest rate, even if you would have qualified for a lower rate:
              </p>

              <div className="space-y-4">
                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <XCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                  <p className="text-primary-foreground">
                    <strong>You may have paid more interest than necessary</strong> — potentially thousands of pounds over the agreement.
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <XCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                  <p className="text-primary-foreground">
                    <strong>The dealer benefited financially</strong> — from increasing your costs, not helping you.
                  </p>
                </div>

                <div className="bg-primary-foreground/10 rounded-xl p-6 flex items-start gap-4">
                  <XCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                  <p className="text-primary-foreground">
                    <strong>You weren't given the opportunity</strong> — to make an informed decision about your finance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Statistics */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  How DCAs Affected Consumers
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                Research and regulatory findings suggest that DCAs had a significant impact:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="text-center border-gold/50">
                  <CardContent className="p-8">
                    <div className="text-4xl font-bold text-gold mb-3">£7.5bn</div>
                    <p className="text-sm text-muted-foreground">
                      FCA estimate of total redress to consumers
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center border-accent/50">
                  <CardContent className="p-8">
                    <div className="text-4xl font-bold text-accent mb-3">£830</div>
                    <p className="text-sm text-muted-foreground">
                      Estimated average compensation per affected agreement
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-muted/50">
                <CardContent className="p-6">
                  <p className="text-muted-foreground text-center">
                    These figures are estimates and individual amounts will vary based on specific circumstances.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Other Forms of Mis-selling */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                Other Forms of PCP Mis-selling
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                Beyond DCAs, PCP finance may have been mis-sold in other ways:
              </p>

              <div className="space-y-6">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-3">Lack of Affordability Assessment</h3>
                    <p className="text-muted-foreground">
                      Lenders have a responsibility to ensure that finance is affordable for the borrower. 
                      If proper affordability checks were not conducted, this could constitute a regulatory breach.
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-3">Inadequate Information</h3>
                    <p className="text-muted-foreground mb-3">
                      You should have been provided with clear information about:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                        <span>The total amount payable including interest</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                        <span>The APR (Annual Percentage Rate)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                        <span>Your options at the end of the agreement</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-gold flex-shrink-0 mt-1" />
                        <span>Mileage limits and excess charges</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-foreground mb-3">Unsuitable Products</h3>
                    <p className="text-muted-foreground">
                      If PCP was not suitable for your circumstances (for example, if HP would have been 
                      more appropriate based on your needs), this could also be grounds for a complaint.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FCA Response Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The FCA's Response
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                The FCA has taken significant action in response to concerns about PCP mis-selling:
              </p>

              <div className="space-y-4">
                {[
                  {
                    date: "January 2021",
                    title: "DCAs Banned",
                    desc: "Dealers can no longer increase interest rates to earn more commission."
                  },
                  {
                    date: "2024",
                    title: "Investigation Completed",
                    desc: "The FCA completed its investigation into how consumers were affected by DCAs."
                  },
                  {
                    date: "2025",
                    title: "Consumer Redress Scheme Introduced",
                    desc: "The FCA has introduced a consumer redress scheme for efficient compensation."
                  }
                ].map((item, idx) => (
                  <Card key={idx} className="border-l-4 border-l-accent">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="bg-accent/10 px-3 py-1 rounded text-sm font-bold text-accent flex-shrink-0">
                        {item.date}
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground">{item.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters */}
        <section className="py-16 lg:py-20 bg-gold/5">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                Why This Matters Now
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                If you took out PCP car finance between <strong className="text-foreground">April 2007 and January 2021</strong>, you may have been 
                affected by DCAs. The FCA's investigation covers this period, and affected consumers 
                may be entitled to compensation.
              </p>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    <strong>Important:</strong> Not everyone who took out PCP finance during this period was affected. 
                    Whether you have a valid claim depends on your individual circumstances, including whether 
                    DCAs were used in your specific agreement and how the finance was sold to you.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Was Your PCP Finance Mis-Sold?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Find out if you're entitled to compensation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="/gap-insurance-claims">
                    GAP Insurance Claims
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/pcp/how-to-check-if-your-pcp-was-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How to Check if Your PCP was Mis-sold</span>
                </a>
                <a href="/pcp/fca-motor-finance-compensation-scheme" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">FCA Motor Finance Scheme</span>
                </a>
                <a href="/gap-insurance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">GAP Insurance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HowPCPIsMisSoldPage;
